import { ReactElement, useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'platform/components/shared/button/Button';
import { useFlags } from '@flagsmith/flagsmith/react';
import { MenuCartItem } from '../../models/menu.types';
import LoadingForkSpoon from '../shared/loading/CookingLoader/LoadingForkSpoon';
import { ProductTileShimmer } from '../product-tile/ProductTileShimmer';
import { initialDropdownFilter, TopLevelFilters } from '../../constants/menu';
import ZeroResultsPlaceholder from './ZeroResultsPlaceholder';
import styles from './MenuPage.module.scss';
import { useMenuFiltering } from '../../hooks/useMenuFiltering';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import ProductCards from './ProductCards';
import { MerchandisingGroup } from './MerchandisingGroup';
import { MerchlessProductCards } from './MerchlessProductCards';

type Props = {
  menuItems: MenuCartItem[];
  onAddToCart: (menuItem: MenuCartItem) => void;
  onUpdateCartQuantity: (cartItem: MenuCartItem, quantity: number) => void;
  isSkipped?: boolean;
  isActive: boolean;
  isMenuLoading?: boolean;
  animateTiles: boolean
  handleSoldOutLtoClick: () => void;
  isLoggedIn?: boolean;
  searchQuery?: string;
  isUserInitiatedSearch: boolean;
  isUserInitiatedFilter: boolean;
};

export function MenuPage({
  menuItems,
  onAddToCart,
  onUpdateCartQuantity,
  isSkipped = false,
  isActive,
  isMenuLoading,
  animateTiles,
  handleSoldOutLtoClick,
  isLoggedIn,
  searchQuery = '',
  isUserInitiatedSearch,
  isUserInitiatedFilter,
}: Props): ReactElement {
  const navigate = useNavigate();
  const {
    menu,
    selectedFilters,
    setSelectedFilters,
    selectedDropdownFilters,
    setSelectedDropdownFilters,
    setFilteredResultCount,
  } = useMenuWithCart();
  const {
    fullyFilteredMenuItems,
    partitionedMenuItems,
    topLevelFilter,
  } = useMenuFiltering({
    menuItems,
    selectedFilters,
    selectedDropdownFilters,
    setFilteredResultCount,
  });

  const [searchParams] = useSearchParams();

  const flags = useFlags(['default_filter_experience']);
  const showGroupsParam = searchParams.get('show_groups');
  const isDefaultFilterExperienceEnabled = flags.default_filter_experience.enabled && flags.default_filter_experience.value === 'show';
  // When hideMerchandisingGroups is true and a filter is active, merch group headers/separators
  // are hidden and items are rendered as a flat list via MerchlessProductCards.
  // - Flag off + show_groups=true  -> shows merch groups
  // - Flag off + no param          -> does not show merch groups
  // - Flag on                      -> shows merch groups
  // - show_groups=false            -> does not show merch groups
  const hideMerchandisingGroups = (!isDefaultFilterExperienceEnabled && showGroupsParam !== 'true') || showGroupsParam === 'false';

  const header = searchParams.get('header') || '';
  const allowToUseHeaderOnSearchHeader = header !== '' && (!isUserInitiatedSearch && !isUserInitiatedFilter);
  const searchHeader = allowToUseHeaderOnSearchHeader ? header : `Results for "${searchQuery}"`;

  // Hide/show meal type headers in menu page
  // TODO: add in real feature flag when available
  const isMealTypeHeaderVisible = false;

  const activeTopLevelFilter = Object.keys(selectedFilters)[0] as TopLevelFilters;
  const activeTopLevelTags = selectedFilters[activeTopLevelFilter] ?? [];

  const isFilterActive = activeTopLevelFilter !== TopLevelFilters.ViewAll
    || activeTopLevelTags.some((t) => t !== 'All')
    || selectedDropdownFilters.productType.length > 0
    || selectedDropdownFilters.cookTime.length > 0;

  const activeFilterCount = (activeTopLevelFilter !== TopLevelFilters.ViewAll ? 1 : 0)
    + activeTopLevelTags.filter((t) => t !== 'All').length
    + selectedDropdownFilters.productType.length
    + selectedDropdownFilters.cookTime.length;

  const cookTimeLabelMap: Record<string, string> = {
    '2 min': '2 min or less',
    '<15 min': '15 min or less',
    '<30 min': '30 min or less',
    '30+ min': '30+ min',
  };

  const activeFilterLabel = (() => {
    if (activeFilterCount > 1) return 'Results for Multiple Filters';
    if (activeTopLevelFilter !== TopLevelFilters.ViewAll) return `Results for ${activeTopLevelFilter}`;
    const activeTag = activeTopLevelTags.find((t) => t !== 'All');
    if (activeTag) return `Results for ${activeTag}`;
    const cookTimeValue = selectedDropdownFilters.cookTime[0];
    const label = selectedDropdownFilters.productType[0] || (cookTimeValue ? cookTimeLabelMap[cookTimeValue] ?? cookTimeValue : '') || '';
    return label ? `Results for ${label}` : '';
  })();

  const groupedMenuItemIds = new Set(
    menu?.merchandisingGroups?.flatMap(
      (g) => g.merchandisingGroupMenuItems.map((mgmi) => mgmi.menuItemId),
    ) ?? [],
  );

  const ungroupedMenuItems = fullyFilteredMenuItems
    .filter((mi) => !groupedMenuItemIds.has(mi.id))
    .sort((a, b) => (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity));

  const allUngroupedMenuItems = menuItems
    .filter((mi) => !groupedMenuItemIds.has(mi.id))
    .sort((a, b) => (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity));

  const [searchResultCount, setSearchResultCount] = useState<number | null>(null);

  useEffect(() => {
    if (searchQuery && searchResultCount !== null) {
      setFilteredResultCount(searchResultCount);
    }
  }, [searchQuery, searchResultCount, setFilteredResultCount]);
  const isSearchEmpty = !isMenuLoading && menuItems.length > 0 && searchResultCount === 0;
  if (!searchQuery && !isMenuLoading && menuItems.length > 0
    && ((topLevelFilter === TopLevelFilters.ViewAll && partitionedMenuItems.length === 0)
      || fullyFilteredMenuItems.length === 0)) {
    return (
      <ZeroResultsPlaceholder resetFilters={() => {
        setSelectedFilters({ [TopLevelFilters.ViewAll]: ['All'] }, true);
        setSelectedDropdownFilters(initialDropdownFilter, true);
      }}
      />
    );
  }

  return (
    <AnimatePresence>
      {isMenuLoading ? (
        <>
          <div
            className={`${styles.product_tiles_display} ${isSkipped ? styles.is_skipped : ''}`}
          >
            <div className={styles.loading_fork_container}>
              <LoadingForkSpoon />
            </div>
            {Array.from({ length: 12 })
              .map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <ProductTileShimmer key={index} />
              ))}
          </div>
        </>
      ) : (
        <>
          {searchQuery ? (
            <>
              <div style={{ display: isSearchEmpty ? 'none' : undefined }}>
                {!!searchResultCount && (
                  <h2 className={styles.search_results_heading}>
                    {searchHeader}
                  </h2>
                )}
                <ProductCards
                  menuItemArr={fullyFilteredMenuItems}
                  searchQuery={searchQuery}
                  onResultCount={setSearchResultCount}
                  isSkipped={isSkipped}
                  animateTiles={animateTiles}
                  isActive={isActive}
                  isLoggedIn={isLoggedIn}
                  onAddToCart={onAddToCart}
                  onUpdateCartQuantity={onUpdateCartQuantity}
                  handleSoldOutLtoClick={handleSoldOutLtoClick}
                  data-heap-merch-group-title="Search Results"
                />
                {!!searchResultCount && (
                  <div className={styles.search_results_heading}>
                    <h2>
                      Want to see more?
                    </h2>
                    <Button
                      text="View the Full Menu"
                      onClick={() => navigate(isLoggedIn ? '/upcoming-menus' : '/shop')}
                    />
                  </div>
                )}
              </div>

              {isSearchEmpty && (
                <>
                  <div className={styles.search_empty_state}>
                    <h2>
                      No results for
                      {' '}
                      <span>
                        &quot;
                        {searchQuery}
                        &quot;
                      </span>
                    </h2>
                    <Button
                      onClick={() => navigate(isLoggedIn ? '/upcoming-menus' : '/shop')}
                      text="Clear search results"
                    />
                    <hr className={styles.search_divider} />
                  </div>
                  {menu?.merchandisingGroups?.map((group, index) => (
                    <MerchandisingGroup
                      index={index}
                      key={group.id}
                      group={group}
                      menuItems={menuItems}
                      isSkipped={isSkipped}
                      animateTiles={animateTiles}
                      isActive={isActive}
                      isLoggedIn={isLoggedIn}
                      onAddToCart={onAddToCart}
                      onUpdateCartQuantity={onUpdateCartQuantity}
                      handleSoldOutLtoClick={handleSoldOutLtoClick}
                      data-heap-merch-group-title={group.title}
                      isUserInitiatedFilter={isUserInitiatedFilter}
                      isUserInitiatedSearch={isUserInitiatedSearch}
                    />
                  ))}
                  <ProductCards
                    menuItemArr={allUngroupedMenuItems}
                    isSkipped={isSkipped}
                    animateTiles={animateTiles}
                    isActive={isActive}
                    isLoggedIn={isLoggedIn}
                    onAddToCart={onAddToCart}
                    onUpdateCartQuantity={onUpdateCartQuantity}
                    handleSoldOutLtoClick={handleSoldOutLtoClick}
                    data-heap-merch-group-title="Not Assigned"
                  />
                </>
              )}
            </>
          ) : (
            <>
              {/* Default view: show merch groups with headers. Rendered when the flag is off,
                  or when the flag is on but no filter is currently active. */}
              {(!hideMerchandisingGroups || !isFilterActive) && (
                <>
                  {menu?.merchandisingGroups?.map((group, index) => (
                    <MerchandisingGroup
                      index={index}
                      key={group.id}
                      group={group}
                      menuItems={fullyFilteredMenuItems}
                      isSkipped={isSkipped}
                      animateTiles={animateTiles}
                      isActive={isActive}
                      isLoggedIn={isLoggedIn}
                      onAddToCart={onAddToCart}
                      onUpdateCartQuantity={onUpdateCartQuantity}
                      handleSoldOutLtoClick={handleSoldOutLtoClick}
                      data-heap-merch-group-title={group.title}
                      isUserInitiatedFilter={isUserInitiatedFilter}
                      isUserInitiatedSearch={isUserInitiatedSearch}
                    />
                  ))}

                  <ProductCards
                    menuItemArr={ungroupedMenuItems}
                    isSkipped={isSkipped}
                    animateTiles={animateTiles}
                    isActive={isActive}
                    isLoggedIn={isLoggedIn}
                    onAddToCart={onAddToCart}
                    onUpdateCartQuantity={onUpdateCartQuantity}
                    handleSoldOutLtoClick={handleSoldOutLtoClick}
                    data-heap-merch-group-title="Not Assigned"
                  />
                </>
              )}
              {/* Filter active + flag on: collapse merch group separators and render
                  all filtered items as a flat list in merchandising order. */}
              {hideMerchandisingGroups && isFilterActive && (
                <MerchlessProductCards
                  activeFilterLabel={allowToUseHeaderOnSearchHeader ? header : activeFilterLabel}
                  menuItems={fullyFilteredMenuItems}
                  isSkipped={isSkipped}
                  animateTiles={animateTiles}
                  isActive={isActive}
                  isLoggedIn={isLoggedIn}
                  onAddToCart={onAddToCart}
                  onUpdateCartQuantity={onUpdateCartQuantity}
                  handleSoldOutLtoClick={handleSoldOutLtoClick}
                />
              )}
            </>
          )}
        </>
      )}
    </AnimatePresence>
  );
}
